import styles from "./styles.module.css";
import { getTable } from "../../../utils/databaseactions";
import { useParams } from "react-router-dom";
import { useState, useContext } from "react";
import { DatabaseValue } from "../../../contexts/database";
import { UserValue } from "../../../contexts/user";
import { useQuery } from "react-query";
import { TypesValue } from "../../../contexts/types";
import { MdAdd } from "react-icons/md";
import Reference from "./referenceBox";
import DataModal from "./dataModal";
import DataEditor from "./dataEditor";
import ImportModal from "./importModal";
import { BsBoxArrowInUpRight } from "react-icons/bs";

export default function Data({ id, tableId, open }) {
  const [importModalIsOpen, setImportModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [addModalIsOpen, setAddModalIsOpen] = useState(false);
  const [activeRef, setActiveRef] = useState(null);
  const [modalData, setModalData] = useState(null);
  const context = useContext(DatabaseValue);
  const user = useContext(UserValue);
  const types = useContext(TypesValue);
  const table = context[id];
  const { appId } = useParams();

  const { data, error, isLoading, isError, refetch } = useQuery(["tableData", tableId], () => {
    const response = getTable(appId, tableId, user);
    return response
    .then(data => {
      if (!data) throw new Error("data is undefined");
      if (data.err) throw data.err;
      return data;
    })
    .catch(err => {
      console.log(err.details);
      throw err.details;
    });
  }, {staleTime: 60000});

  const toggleActiveRef = (key) => {
    if (key === activeRef) setActiveRef(null);
    else setActiveRef(key);
  }

  const mapDbHeader = (_id) => {
    try {
      const type = table.fields[_id].type.toLowerCase();
      const name = table.fields[_id].name;
      const isList = table.fields[_id].list;
      for (let i = 0; i < types.length; i++) {
        const t = types[i];
        if (t.name.toLowerCase() === type) {
          t["list"] = isList;
          return {name: name, type: t};
        }
      }
      throw new Error("type not found in Types context");
    } 
    catch (err) { 
      return {
        name: _id,
        list: false,
        type: {
          type: "Text",
          name: _id
        }
      };
    }
  }

  const formatDate = (d) => {
    const date = new Date(d);
    const mm = (date.getMonth() + 1).toString().padStart(2, "0");
    const dd = date.getDate().toString().padStart(2, "0");
    return `${mm}-${dd}-${date.getFullYear()}`;
  }

  const formatData = (key, data, type) => {
    if (!data) return <td key={key}></td>;
    try {
      if (type.name === "Date") {
        const date = formatDate(data);
        return <td key={key}>{date}</td>;
      }
      if (type.name === "True/False") {
        const text = data ? "True" : "False"; 
        return <td key={key}>{text}</td>;
      }
      if (type.name === "Location") {
        let lat, lng, text;
        if (typeof data === "string") {
          data = data.replace(/"/g, "").replace("}", "");
          [lat, lng] = data.split(",");
          text = `${lat.split(":")[1]}, ${lng.split(":")[1]}`;
        }
        else text = `${data.lat}, ${data.lng}`;
        return <td key={key}>{text}</td>;
      }
      if (type.name === "Image" || type.name === "File") {
        const name = data ? data.name : "";
        const text = name.length > 28 ? name.substr(0, 25) + "..." : name;
        const src = data ? data.src : "";
        return(
          <td key={key}>
            <a href={src} target="_blank" className={styles.link} title={name}>
              {text}
              <span>
                <BsBoxArrowInUpRight />
              </span>
            </a>
          </td>
        )
      }
      if (type.type === "database" || type.category === "database") {
        return (
          <td key={key} className={styles.referenceContainer}>
            <Reference 
              setActive={() => toggleActiveRef(key)} 
              active={activeRef === key}
              type={type.displayName} 
              table={type.name} 
              id={data} 
              list={type.list} />
          </td>
        );
      }
    }
    catch (err) {
      console.log(err);
    }
    if (typeof data === "boolean") return <td key={key} title={data ? "True" : "False"}>{data}</td>
    if (typeof data === "object") return <td key={key}>{data.toString()}</td>
    return <td key={key} title={data}>{data}</td>
  }

  const openModal = (d = {}) => {
    setModalData(d);
    setEditModalIsOpen(true);
  }

  const updateData = () => {
    refetch();
  }

  const renderTable = () => {
    if (isLoading) {
      return <div>Loading Data...</div>
    }
    if (isError) { 
      return <div>Error: {error.message}</div>
    }
    if (data.length === 0) {
      return <div>Table is empty</div>
    }

    try {
      const dbHeaders = Object.keys(data[0]);
      const fbHeaders = {};
      dbHeaders.forEach(cur => {
        fbHeaders[cur] = mapDbHeader(cur);
      });

      return (
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th></th>
              {Object.keys(fbHeaders || {}).map(cur => <th key={`${tableId}.${cur}`}>{fbHeaders[cur].name}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.map((cur, row) => (
              <tr key={`${tableId}.${row}`}>
                <td>
                  <button className={styles.editButton} onClick={() => openModal(cur)}>Edit</button>
                </td>
                {Object.keys(fbHeaders || {}).map((f, col) => (
                  formatData(`${tableId}.${row}.${col}`, cur[f], fbHeaders[f].type)
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )
    }
    catch (err) {
      return <div>Table is empty</div>
    }
  }

  if (!open) return null;
  return (
      <div className={styles.section} style={{position: "relative"}}>
        <div className={styles.dataContainer}>
          <div className={styles.newField} onClick={() => setImportModalIsOpen(true)}>
            <div style={{marginRight: 7}}>Import</div>
          </div>
          <div className={styles.newField} onClick={() => setAddModalIsOpen(true)}>
            <MdAdd />
            <div style={{marginLeft: 7}}>Add</div>
          </div>
          <div className={styles.newField} onClick={refetch}>
            Refresh
          </div>
        </div>
        <div className={styles.data}>
          {renderTable()}
        </div>
        <ImportModal 
          table={table.id} 
          fields={table.fields} 
          setOpen={setImportModalIsOpen}
          open={importModalIsOpen}
          updateData={updateData}
          />
        <DataEditor 
          initialData={modalData}
          updateData={updateData}
          setOpen={setEditModalIsOpen} 
          open={editModalIsOpen} 
          tableId={table.id}
          fields={table.fields} />
        <DataModal 
          updateData={updateData}
          setOpen={setAddModalIsOpen} 
          open={addModalIsOpen} 
          tableId={table.id}
          fields={table.fields} />
      </div>
  )
}
